import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2, CalendarDays, Download, Clock, Save } from "lucide-react";
import { toast } from "sonner";
import api, { downloadBlob, filenameFromHeader, errorMessage } from "@/lib/api";

const STATUSES = [
  { key: "present", label: "Present", cls: "bg-emerald-500 text-white", off: "text-emerald-700 border-emerald-200" },
  { key: "half-day", label: "Half-day", cls: "bg-amber-500 text-white", off: "text-amber-700 border-amber-200" },
  { key: "leave", label: "Leave", cls: "bg-sky-500 text-white", off: "text-ark-skyDeep border-sky-200" },
  { key: "absent", label: "Absent", cls: "bg-red-500 text-white", off: "text-red-600 border-red-200" },
];

const todayStr = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const monthStr = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 7);

const SchoolHoursCard = () => {
  const [hours, setHours] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/school-hours")
      .then(({ data }) => setHours(data))
      .catch(async (err) => toast.error(await errorMessage(err, "Failed to load school hours.")));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings/school-hours", hours);
      setHours(data);
      toast.success("School hours saved. Late & Early flags will use the new times.");
    } catch (err) { toast.error(await errorMessage(err, "Could not save hours.")); }
    finally { setSaving(false); }
  };

  if (!hours) return null;
  const HourInput = ({ id, label }) => (
    <div>
      <Label className="text-xs font-semibold text-slate-700">{label}</Label>
      <Input type="time" value={hours[id] || ""} data-testid={`school-hours-${id}`}
        onChange={(e) => setHours((h) => ({ ...h, [id]: e.target.value }))}
        className="mt-1.5 h-10 rounded-2xl border-slate-200" />
    </div>
  );
  return (
    <Card className="border-0 rounded-3xl shadow-ark-soft" data-testid="school-hours-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-ark-teal" />
            <h3 className="font-display font-bold text-lg text-ark-ink">School Hours</h3>
          </div>
          <Button onClick={save} disabled={saving} data-testid="school-hours-save"
            className="rounded-full h-10 px-5 bg-ark-teal hover:bg-ark-tealDeep text-white font-display font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Staff who check in after the check-in window are auto-flagged LATE; those who check out before the check-out window are flagged EARLY. Timings are in IST.</p>
        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <HourInput id="checkin_start" label="Check-in opens at" />
          <HourInput id="checkin_end" label="On-time until" />
          <HourInput id="checkout_start" label="Check-out opens at" />
          <HourInput id="checkout_end" label="Latest check-out" />
        </div>
      </CardContent>
    </Card>
  );
};

const AttendancePanel = () => {
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportMonth, setExportMonth] = useState(monthStr());
  const [exporting, setExporting] = useState(false);

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/attendance", { params: { date: d } });
      setRows(data.staff);
    } catch { toast.error("Failed to load attendance."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(date); /* eslint-disable-next-line */ }, [date]);

  const mark = async (staff_email, status) => {
    setRows((r) => r.map((x) => x.staff_email === staff_email ? { ...x, status } : x));
    try {
      await api.post("/admin/attendance", { staff_email, date, status });
      toast.success("Attendance saved.");
    } catch { toast.error("Could not save."); load(date); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/attendance/export", {
        params: { month: exportMonth }, responseType: "blob",
      });
      const name = filenameFromHeader(res.headers, `attendance_${exportMonth}.csv`);
      downloadBlob(res.data, name);
      toast.success(`Exported ${exportMonth}`);
    } catch (err) { toast.error(await errorMessage(err, "Could not export CSV.")); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6">
      <SchoolHoursCard />
      <Card className="border-0 rounded-3xl shadow-ark-soft" data-testid="attendance-panel">
      <CardContent className="p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <h3 className="font-display font-bold text-lg text-ark-ink">Daily Attendance</h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative">
              <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input type="date" value={date} data-testid="attendance-date"
                onChange={(e) => setDate(e.target.value)}
                className="pl-10 h-11 rounded-2xl border-slate-200 w-full sm:w-48" />
            </div>
            <div className="flex items-center gap-2">
              <Input type="month" value={exportMonth} data-testid="attendance-export-month"
                onChange={(e) => setExportMonth(e.target.value || monthStr())}
                aria-label="Export month" className="h-11 rounded-2xl border-slate-200 w-full sm:w-40" />
              <Button onClick={exportCsv} disabled={exporting} data-testid="attendance-export-btn"
                variant="outline" className="h-11 rounded-full border-ark-teal text-ark-tealDeep font-display font-semibold px-4">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Export CSV</>}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500">No staff yet. Add staff first.</div>
          ) : rows.map((s) => (
            <div key={s.staff_email} data-testid={`attendance-row-${s.staff_email}`}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full overflow-hidden bg-gradient-to-br from-ark-sky to-ark-teal flex items-center justify-center font-display font-bold text-white shrink-0">
                  {s.photo ? <img src={s.photo} alt="" className="h-full w-full object-cover" /> : (s.staff_name || s.staff_email).charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-ark-ink truncate">{s.staff_name || s.staff_email}</div>
                  <div className="text-xs text-slate-500 truncate">{s.designation || "Staff"}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label={`Attendance status for ${s.staff_name || s.staff_email}`}>
                {STATUSES.map((st) => {
                  const isActive = s.status === st.key;
                  return (
                    <button key={st.key} data-testid={`att-${st.key}-${s.staff_email}`}
                      onClick={() => mark(s.staff_email, st.key)}
                      aria-pressed={isActive}
                      aria-label={`Mark ${s.staff_name || s.staff_email} as ${st.label}`}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${isActive ? st.cls + " border-transparent" : "bg-white " + st.off + " hover:bg-slate-50"}`}>
                      {st.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
    </div>
  );
};

export default AttendancePanel;
